import React, { type RefObject, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Menu, { MenuItem } from '@amzn/meridian/menu';
import {
  ALTERNATIVE_PLACEMENT_RECOMMENDED,
  APPROVED_TWP,
  ASSOCIATE_DECISION_PENDING,
  DENIED_TWP,
  DLS_REFERRAL_RECEIVED,
  INDEFINITE_RESTRICTIONS,
  PLACEMENT_DECISION_PENDING,
  TWP_BUT_PENDING_UPDATED_RESTRICTIONS,
} from 'src/config/AccommodationStatusNames';
import { useRestrictionValidation } from 'src/hooks/useRestrictionValidation';
import JmrService from 'src/services/jmr/jmrService';
import {
  initializeEditAssociateModal,
  setIsUpdateStatusModalOpen,
} from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import type { AccommodationStatus, PlacementDetailsWorkRestriction } from 'src/store/placementDetailsPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { constructMedicalAttachmentsLink } from 'src/utils/constructRTWLinks';
import { JmrFlowTypes } from 'src/utils/jmrUtils';
import type { RestrictionListParentTask, RestrictionListTask } from 'src/utils/restrictionUtilsLegacy';

import ConfirmUpdateRestrictionsModal from '../pages/placementDetails/confirmUpdateRestrictionsModal/confirmUpdateRestrictionsModal';
import EditAssociateInfoModalAUSTIN from '../pages/placementDetails/editAssociateInfoModal/editAssociateInfoModal';
import InitiateTransferModal from '../pages/placementDetails/initiateTransferModal/initiateTransferModal';

import ConfirmModal from './confirmModal/confirmModal';

interface PlacementUpdateMenuProps {
  placementId: string;
  anchorRef: RefObject<HTMLButtonElement>;
  isMenuOpen: boolean;
  closeMenu: VoidFunction;
  accommodationStatusesForSite: AccommodationStatus[];
  managerAlias: string;
  fc: string;
  accommodationType: string;
  workRestrictions: PlacementDetailsWorkRestriction[];
  modifiedShiftLength: number;
  placementStatus: string;
  externalCaseId: string;
  regionTasks: (RestrictionListTask | RestrictionListParentTask)[];
  showAdminEdit?: boolean;
}

const PlacementUpdateMenu = ({
  placementId,
  anchorRef,
  isMenuOpen,
  closeMenu,
  accommodationStatusesForSite,
  managerAlias,
  fc,
  accommodationType,
  workRestrictions,
  modifiedShiftLength,
  placementStatus,
  externalCaseId,
  regionTasks,
  showAdminEdit = false,
}: PlacementUpdateMenuProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { featureFlags } = useAppSelector((state) => state.user);

  const [isConfirmUpdateRestrictionsModalOpen, setIsConfirmUpdateRestrictionsModalOpen] = useState(false);
  const [isInitiateTransferModalOpen, setIsInitiateTransferModalOpen] = useState(false);
  const [isRestrictionEntriesErrorModalOpen, setIsRestrictionEntriesErrorModalOpen] = useState(false);
  const [isAlternativePlacementModalOpen, setIsAlternativePlacementModalOpen] = useState(false);
  const [isUpdateRestrictionDatesModalOpen, setIsUpdateRestrictionDatesModalOpen] = useState(false);
  const [isUploadRfiModalOpen, setIsUploadRfiModalOpen] = useState(false);
  const doesPlacementHaveRestrictionErrors = useRestrictionValidation(
    regionTasks,
    workRestrictions,
    modifiedShiftLength,
  );

  // These statuses are allowed to enter the update placement flow
  const STATUSES_ALLOWED_TO_UPDATE_PLACEMENTS = featureFlags.DECISION_PORTAL
    ? [
        PLACEMENT_DECISION_PENDING,
        ASSOCIATE_DECISION_PENDING,
        APPROVED_TWP,
        INDEFINITE_RESTRICTIONS,
        TWP_BUT_PENDING_UPDATED_RESTRICTIONS,
        DLS_REFERRAL_RECEIVED,
        DENIED_TWP,
      ]
    : [PLACEMENT_DECISION_PENDING, ALTERNATIVE_PLACEMENT_RECOMMENDED];

  // Navigate to update JMR flow
  const navigateToUpdateJmr = (updateType: JmrFlowTypes) => {
    navigate(`/newJMR/${placementId}/update/${updateType}`);
  };

  // Navigate to Confirm update restrictions from the modal
  const confirmUpdateRestrictions = async () => {
    if (placementId && !doesPlacementHaveRestrictionErrors) {
      const id = parseInt(placementId);
      const { data: isUpdateRestrictionAllowed } = await JmrService.getUpdateRestrictionAllowed(id);
      if (isUpdateRestrictionAllowed) {
        navigateToUpdateJmr(JmrFlowTypes.NewRestrictions);
      } else {
        setIsConfirmUpdateRestrictionsModalOpen(false);
        setIsUploadRfiModalOpen(true);
      }
    } else {
      navigateToUpdateJmr(JmrFlowTypes.NewRestrictions);
    }
  };

  const initiateTransferActionClickHandler = () => {
    if (doesPlacementHaveRestrictionErrors) {
      setIsRestrictionEntriesErrorModalOpen(true);
    } else {
      setIsInitiateTransferModalOpen(true);
    }
  };

  const placementSelectionActionClickHandler = () => {
    if (doesPlacementHaveRestrictionErrors) {
      setIsRestrictionEntriesErrorModalOpen(true);
    } else {
      setIsAlternativePlacementModalOpen(true);
    }
  };

  const restrictionDatesActionClickHandler = () => {
    if (doesPlacementHaveRestrictionErrors) {
      setIsRestrictionEntriesErrorModalOpen(true);
    } else {
      setIsUpdateRestrictionDatesModalOpen(true);
    }
  };

  return (
    <>
      <Menu
        anchorNode={anchorRef.current ?? undefined}
        open={isMenuOpen}
        position="bottom"
        onClose={closeMenu}
        data-cy="placement-detail__update-menu"
        minWidth="200px"
      >
        {(accommodationStatusesForSite?.length ?? 0) > 0 && (
          <MenuItem onClick={() => dispatch(setIsUpdateStatusModalOpen(true))} data-cy="placement-details__status-btn">
            Status
          </MenuItem>
        )}
        <MenuItem onClick={restrictionDatesActionClickHandler} data-cy="placement-details__extend-end-date-btn">
          Restriction Dates
        </MenuItem>
        <MenuItem
          onClick={() => setIsConfirmUpdateRestrictionsModalOpen(true)}
          data-cy="placement-details__restrictions-btn"
        >
          Restrictions
        </MenuItem>
        {STATUSES_ALLOWED_TO_UPDATE_PLACEMENTS.includes(placementStatus) && (
          <MenuItem
            onClick={placementSelectionActionClickHandler}
            data-cy="placement-details__alternative-placement-btn"
          >
            Placement Selection(s)
          </MenuItem>
        )}
        {accommodationType === 'AUSTIN' && (
          <MenuItem onClick={initiateTransferActionClickHandler} data-cy="placement-details__initiate-transfer-btn">
            Initiate Transfer
          </MenuItem>
        )}
        {showAdminEdit && (
          <MenuItem
            onClick={() => dispatch(initializeEditAssociateModal())}
            data-cy="placement-details__admin-edit-btn"
            data-testid="placement-details__admin-edit-btn"
          >
            Admin Edit
          </MenuItem>
        )}
      </Menu>
      <ConfirmModal
        modalTitle="Update Placement Restrictions?"
        modalDescription="If restrictions and start/end dates have changed, Restrictions will navigate you to a page where 
                you will enter new restrictions and dates."
        open={isConfirmUpdateRestrictionsModalOpen}
        setOpen={(open) => setIsConfirmUpdateRestrictionsModalOpen(open)}
        onConfirm={confirmUpdateRestrictions}
      />
      <ConfirmModal
        modalTitle="Upload RFI or WSR?"
        modalDescription="You must upload a new RFI or WSR to update the restrictions for this placement. If a new RFI or WSR has 
          already been uploaded, please wait a few minutes and try again."
        open={isUploadRfiModalOpen}
        setOpen={(open) => setIsUploadRfiModalOpen(open)}
        onConfirm={() => setIsUploadRfiModalOpen(false)}
        includeLink={true}
        linkText="Austin Incident Link"
        linkUrl={externalCaseId && constructMedicalAttachmentsLink(externalCaseId)}
        showCancelButton={false}
      />
      <ConfirmModal
        modalTitle="Select Alternative Placements?"
        modalDescription="If the site decision maker accepted an alternative placement, Placement Selections(s) will navigate you 
              to a page where you will select new placements."
        open={isAlternativePlacementModalOpen}
        setOpen={(open) => setIsAlternativePlacementModalOpen(open)}
        onConfirm={() => navigateToUpdateJmr(JmrFlowTypes.AlternativePlacementRecommended)}
      />
      <ConfirmModal
        modalTitle="Update Restriction Dates?"
        modalDescription="If ONLY start/end dates have changed (restrictions have remained the same), Restriction Dates will navigate 
              you to a page where you will select new start and end dates."
        open={isUpdateRestrictionDatesModalOpen}
        setOpen={(open) => setIsUpdateRestrictionDatesModalOpen(open)}
        onConfirm={() => navigateToUpdateJmr(JmrFlowTypes.UpdateDates)}
      />
      <InitiateTransferModal
        placementId={placementId!}
        managerAlias={managerAlias}
        fc={fc}
        isOpen={isInitiateTransferModalOpen}
        closeModal={() => setIsInitiateTransferModalOpen(false)}
      />
      <ConfirmUpdateRestrictionsModal
        open={isRestrictionEntriesErrorModalOpen}
        onClose={() => setIsRestrictionEntriesErrorModalOpen(false)}
        submit={() => navigateToUpdateJmr(JmrFlowTypes.NewRestrictions)}
      />
      <EditAssociateInfoModalAUSTIN />
    </>
  );
};

export default PlacementUpdateMenu;
